import React from 'react';
import { PanelId, PANELS, PANEL_HEIGHT } from '../panels/registry';
import { panelBackground, panelShadowAlpha } from './Panel';
import { SLIDE_MS, EASING } from '../panels/useStackMotion';

// A scroll control for the panel stack, one at each end of the row.
//
// It sits in the band under the panels where their move/hide tabs hang, pinned
// to the end of the scrolling region rather than to the end of the view: the
// view changes width as it scrolls, and a control that moved with it would
// slide out from under the pointer between one click and the next. The left
// one moves only when a panel is locked or unlocked, to stay past the locked
// panels.
//
// Outside the chevron it carries a miniature of everything waiting beyond
// that edge, like the Settings meter: one block per panel, in stack order,
// each to scale. The scale is fixed rather than fitted to the space, so a
// block means the same width on both sides and a 36dvw panel always reads as
// wider than a 20dvw one.
//
// The blocks slide with the panels. Each track holds a block for every panel
// in the stack, at no width unless that panel waits on its side, so a scroll
// changes widths rather than adding and removing blocks: the blocks against
// the chevron shrink away on one side as their panels come into view, grow in
// on the other as panels leave, and the rest slide along, on the panels' own
// timing. For the same reason the control stays mounted when nothing waits
// beyond its edge, and fades out instead.
//
// The control is a fixed width however many blocks it holds, so the place to
// click never changes size between clicks. The track is sized for the most it
// could ever hold — every panel but the two narrowest, since two are always
// out of the running: any two panels fit the row side by side, and with a
// lock there is the locked panel and at least one in view — and the blocks
// sit against the chevron.

/** How far below the panels' bottom edge the controls sit, in dvw. Negative raises them. */
const DROP = 0.15;
/** Miniature size of a panel width: 1dvw of panel is this many dvw of block. */
const MINI_SCALE = 0.03;
/** Space between two blocks, in dvw. */
const MINI_GAP = 0.12;

const MINI_TRACK = (() => {
  const widths = Object.keys(PANELS).map(id => PANELS[id as PanelId].width).sort((a, b) => a - b);
  const most = widths.slice(2);
  const total = most.reduce((sum, w) => sum + w, 0);
  // Each block carries half a gap either side, so the track allows a whole
  // gap per block.
  return total * MINI_SCALE + MINI_GAP * most.length;
})();

interface StackScrollerProps {
  side: 'left' | 'right';
  /** Every panel in the stack, in order — one block each, so blocks slide rather than appear. */
  stack: PanelId[];
  /** The panels waiting beyond this edge. Only these blocks have any width. */
  beyond: PanelId[];
  appTheme: string;
  onScroll: () => void;
  /** Where the scrolling region starts, in dvw from the row's left edge — past any locked panels. */
  left?: number;
}

const StackScroller: React.FC<StackScrollerProps> = ({ side, stack, beyond, appTheme, onScroll, left }) => {
  const light = appTheme === 'light';

  const style: React.CSSProperties = {
    top: `${PANEL_HEIGHT + DROP}dvw`,
    left: side === 'left' ? `${left === undefined ? 0 : left}dvw` : 'auto',
    right: side === 'right' ? 0 : 'auto',
    background: panelBackground(appTheme, 0.7),
    color: light ? 'rgb(25, 25, 25)' : 'rgb(238, 238, 238)',
    boxShadow: `0 0.3dvw 0.45dvw -0.2dvw rgba(0, 0, 0, ${panelShadowAlpha(appTheme)})`,
  };

  const blockTone = light ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.45)';
  const slide = `width ${SLIDE_MS}ms ${EASING}, margin ${SLIDE_MS}ms ${EASING}`;

  const mini = (
    <span
      className="stack-scroll-mini"
      aria-hidden="true"
      style={{
        width: `${MINI_TRACK}dvw`,
        justifyContent: side === 'left' ? 'flex-end' : 'flex-start',
      }}
    >
      {stack.map(id => {
        const waiting = beyond.indexOf(id) !== -1;
        return (
          <span
            key={id}
            className="stack-scroll-mini-block"
            style={{
              width: waiting ? `${PANELS[id].width * MINI_SCALE}dvw` : 0,
              margin: waiting ? `0 ${MINI_GAP / 2}dvw` : 0,
              background: blockTone,
              transition: slide,
            }}
          />
        );
      })}
    </span>
  );

  return (
    <button
      type="button"
      className={beyond.length === 0 ? 'stack-scroll is-empty' : 'stack-scroll'}
      // Nothing beyond this edge: faded out, and not focusable or clickable.
      tabIndex={beyond.length === 0 ? -1 : undefined}
      aria-hidden={beyond.length === 0 ? true : undefined}
      // Scrolling the panels is not a click away from the chart, so it keeps
      // the chart's chord selection (see handleGlobalClick).
      data-actionable="true"
      style={style}
      onClick={onScroll}
      title={side === 'left' ? 'Slide panels (Shift + ←)' : 'Slide panels (Shift + →)'}
    >
      {side === 'left'
        ? <>{mini}<span className="stack-scroll-chevron">{'‹'}</span></>
        : <><span className="stack-scroll-chevron">{'›'}</span>{mini}</>}
    </button>
  );
};

export default StackScroller;
